import { Modal } from 'components/common/Modal/Modal';
import i18n from 'i18n';
import styles from './outdoorDeviceReport.module.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ToggleButtonField } from 'components/common/form/field/ToggleButtonField';
import { CustomField } from 'components/common/form/field/CustomField';
import ToggleButtons from 'components/common/ToggleButtons/ToggleButtons';
import { DateRangePicker } from 'components/common/DateRangePicker/DateRangePicker';
import moment from 'moment';
import { Trans } from 'react-i18next';
import { Button, NavDropdown } from 'react-bootstrap';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import {
  KEY_COLUMN,
  OutdoorDeviceFilterDimension,
  useOutdoorDeviceReportModel
} from './OutdoorDeviceReportModel';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { commaDecorator } from 'components/ReportTable/ReportDataHelper';
import { ChartOptions } from 'chart.js';
import config from 'config';
import { GenericChart } from 'components/common/Chart/GenericChart';
import {
  faAngleDown,
  faAngleUp,
  faChevronUp,
  faDownload,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames/bind';
import { OutdoorDeviceMetricType } from 'core/report/ReportData';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import {
  WeatherVerificationFilters,
  WeatherVerificationResult
} from './WeatherVerification/WeatherVerificationTab';
import { useWeatherVerificationTabModel } from './WeatherVerification/WeatherVerificationTabModel';
import { supportsDeliveryVerification } from 'core/report/WeatherVerification';
// 匯出列沿用機台投遞驗證的樣式，兩個 Tab 的匯出按鈕外觀一致
import exportStyles from './WeatherVerification/weatherVerificationTab.module.scss';
import { SelectField } from 'components/common/form/field/SelectField';
import Select from 'components/common/Select/Select';
import { WithAddIcon } from 'components/common/WithAddIcon/WithAddIcon';
const cx = classNames.bind(styles);

const FILTER_DIMENSION_LABEL_KEYS: Record<
  OutdoorDeviceFilterDimension,
  string
> = {
  [OutdoorDeviceFilterDimension.CAMPAIGN]:
    'weatherVerification.labels.campaign',
  [OutdoorDeviceFilterDimension.STORE]: 'weatherVerification.labels.store'
};

enum OutdoorReportType {
  PERFORMANCE = 'performance',
  WEATHER_VERIFICATION = 'weatherVerification'
}

export type OutdoorDeviceReportProps = {
  l1ObjectId: number;
  startDate: string;
  endDate: string;
  deliveryUnit?: DeliveryUnit;
  /** 這個廣告群組所屬的銷售渠道，決定看不看得到機台投遞驗證 */
  channelGroupId: number;
  /**
   * 這個廣告群組所屬的銷售渠道是不是電子紙。
   * 電子紙不適用人流數，資料類型的收斂在 useOutdoorDeviceReportModel 裡。
   */
  isEpaper: boolean;
  onClose: () => void;
};

export const OutdoorDeviceReport: React.FC<OutdoorDeviceReportProps> = ({
  l1ObjectId,
  startDate,
  endDate,
  deliveryUnit,
  channelGroupId,
  isEpaper,
  onClose
}) => {
  const [reportType, setReportType] = useState<OutdoorReportType>(
    OutdoorReportType.PERFORMANCE
  );
  const isWeatherVerification =
    reportType === OutdoorReportType.WEATHER_VERIFICATION;
  const {
    loading,
    columns,
    metricType,
    metricTypeOptions,
    gran,
    granOptions,
    from,
    to,
    reportTableData,
    metricTotalSum,
    tableSummaryRecord,
    queryOptions,
    chartData,
    dayRangeError,
    filterRangeExceeded,
    filterRangeHint,
    filterDimensions,
    filterDimensionsCanAdd,
    campaignId,
    storeKeys,
    campaignOptions,
    storeOptions,
    canExportCsv,
    onMetricTypeChange,
    onGranChange,
    onDayRangeChange,
    onAddFilter,
    onRemoveFilter,
    onCampaignChange,
    onStoreChange,
    onQueryBtnClick,
    onExportCsv
  } = useOutdoorDeviceReportModel(
    l1ObjectId,
    startDate,
    endDate,
    deliveryUnit,
    isEpaper,
    !isWeatherVerification
  );
  const weatherVerificationModel = useWeatherVerificationTabModel(
    l1ObjectId,
    startDate,
    endDate,
    isWeatherVerification
  );
  const onReportTypeChange = useCallback((value: string | number) => {
    setReportType(value as OutdoorReportType);
  }, []);
  const reportTypeOptions = useMemo(
    () => [
      {
        value: OutdoorReportType.PERFORMANCE,
        label: i18n.t('weatherVerification.labels.deliveryPerformance')
      },
      ...(supportsDeliveryVerification(channelGroupId)
        ? [
            {
              value: OutdoorReportType.WEATHER_VERIFICATION,
              label: i18n.t('weatherVerification.labels.deliveryVerification')
            }
          ]
        : [])
    ],
    [channelGroupId]
  );

  const [showDimensionSelectArea, setShowDimensionSelectArea] = useState(true);
  const [showReportChart, setShowReportChart] = useState(true);
  const toggleShowDimensionSelectArea = useCallback(() => {
    setShowDimensionSelectArea((prev) => !prev);
  }, []);
  const toggleShowReportChart = useCallback(() => {
    setShowReportChart((prev) => !prev);
  }, []);

  // 篩選區很高、表格標頭又在畫面外，沒辦法做固定表頭；改成標題列捲出畫面後
  // 給一顆回到頂部的按鈕。觀察的是標題列本身而不是特定捲動容器，
  // 所以不必知道實際是哪一層在捲。
  // 用 callback ref 存進 state，標題列節點換掉時 observer 會跟著重建。
  const [title, setTitle] = useState<HTMLDivElement | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    if (!title || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      // 同一批可能排了好幾筆，最後一筆才是現況
      setShowBackToTop(!entries[entries.length - 1].isIntersecting);
    });
    observer.observe(title);
    return () => observer.disconnect();
  }, [title]);
  const backToTop = useCallback(() => {
    // 使用者回頂部是為了調整篩選條件，篩選區收合時一併展開
    setShowDimensionSelectArea(true);
    title?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // 按鈕回到頂部後就會消失，焦點移到標題列，鍵盤使用者才能從篩選區接著操作
    title?.focus({ preventScroll: true });
  }, [title]);

  const renderTitle = useCallback(() => {
    return (
      <div className={styles.titleArea} ref={setTitle} tabIndex={-1}>
        <div className={styles.title}>
          {i18n.t('outdoorDeviceReport.title')}
        </div>
        <div className={styles.triggerAreaButtons}>
          <div
            className={styles.triggerAreaButton}
            onClick={toggleShowDimensionSelectArea}
          >
            <FontAwesomeIcon
              icon={showDimensionSelectArea ? faAngleDown : faAngleUp}
            />
            {i18n.t('report.labels.dimentSelectArea')}
          </div>
          <div
            className={styles.triggerAreaButton}
            onClick={toggleShowReportChart}
          >
            <FontAwesomeIcon icon={showReportChart ? faAngleDown : faAngleUp} />
            {i18n.t('report.labels.reportChartArea')}
          </div>
        </div>
      </div>
    );
  }, [
    showDimensionSelectArea,
    showReportChart,
    toggleShowDimensionSelectArea,
    toggleShowReportChart
  ]);

  const renderScheduling = useCallback((from: string, to: string) => {
    const endDateMoment = moment(to);
    const startDateMoment = moment(from);
    const days = endDateMoment.diff(startDateMoment, 'days');
    const hours = endDateMoment.diff(startDateMoment, 'hours');
    if (days > 0) {
      return (
        <Trans
          i18nKey='campaign.descriptions.campaignSchedulingDay'
          values={{ days: days + 1 }}
          components={{ 1: <span className='text-dark' /> }}
        />
      );
    } else if (days === 0) {
      return (
        <Trans
          i18nKey='campaign.descriptions.campaignSchedulingHour'
          values={{ hours: hours + 1 }}
          components={{ 1: <span className='text-dark' /> }}
        />
      );
    }
  }, []);

  const renderFilter = useCallback(
    (dimension: OutdoorDeviceFilterDimension) => {
      const removeButton = (
        <FontAwesomeIcon
          className={styles.removeFilterBtn}
          icon={faTimesCircle}
          onClick={() => onRemoveFilter(dimension)}
        />
      );
      const label = i18n.t(FILTER_DIMENSION_LABEL_KEYS[dimension]);
      if (dimension === OutdoorDeviceFilterDimension.CAMPAIGN) {
        return (
          <SelectField
            key={dimension}
            label={label}
            name='outdoorDeviceCampaignFilter'
            options={campaignOptions}
            value={campaignId}
            simpleValue
            isClearable
            disabled={filterRangeExceeded}
            placeholder={i18n.t('common.placeholder.pleaseSelect')}
            onChange={onCampaignChange}
            postComponent={removeButton}
            postComponentStyle={styles.removeFilterBtnContainer}
          />
        );
      }
      return (
        <CustomField
          key={dimension}
          label={label}
          name='outdoorDeviceStoreFilter'
          fieldContentWidth={424}
          postComponent={removeButton}
          postComponentStyle={styles.removeFilterBtnContainer}
        >
          <Select
            name='outdoorDeviceStoreFilter'
            options={storeOptions}
            value={storeKeys}
            isMulti
            simpleValue
            isClearable
            isDisabled={filterRangeExceeded}
            maxMenuHeight={200}
            placeholder={i18n.t('common.placeholder.pleaseSelect')}
            onChange={onStoreChange}
          />
        </CustomField>
      );
    },
    [
      campaignId,
      campaignOptions,
      storeKeys,
      storeOptions,
      filterRangeExceeded,
      onCampaignChange,
      onStoreChange,
      onRemoveFilter
    ]
  );

  const renderPerformanceUserOptions = useCallback(() => {
    return (
      <>
        <ToggleButtonField
          label={i18n.t('outdoorDeviceReport.labels.metricType')}
          name='metricType'
          value={metricType}
          options={metricTypeOptions}
          onChange={onMetricTypeChange}
        />
        <CustomField
          label={i18n.t('outdoorDeviceReport.labels.scheduledTimeRange')}
          name='scheduledTimeRange'
        >
          <div className={styles.reportGran}>
            <ToggleButtons
              name={'gran'}
              value={gran}
              options={granOptions}
              onChange={onGranChange}
            />
            <DateRangePicker
              minDate={startDate}
              maxDate={endDate}
              startDate={from}
              endDate={to}
              format={gran === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'}
              onChange={onDayRangeChange}
              showTimePicker={gran === 'hour'}
              popOverClassName={styles.dateRangePicker}
            />
            <span className={styles.hint}>{renderScheduling(from, to)}</span>
            {dayRangeError && (
              <span className={styles.errorHint}>{dayRangeError}</span>
            )}
          </div>
        </CustomField>
        {filterDimensions.map(renderFilter)}
        {(filterDimensionsCanAdd.length > 0 || filterRangeHint) && (
          <CustomField
            label=''
            name='outdoorDeviceFilterAdder'
            hint={filterRangeHint}
          >
            {filterDimensionsCanAdd.length > 0 && (
              <WithAddIcon
                className={cx(styles.filterAdder, {
                  filterAdderDisabled: filterRangeExceeded
                })}
              >
                <NavDropdown
                  title={i18n.t('report.labels.filterCondition')}
                  id='outdoorDeviceFilterList'
                  disabled={filterRangeExceeded}
                >
                  {filterDimensionsCanAdd.map((dimension) => (
                    <NavDropdown.Item
                      key={dimension}
                      onClick={() => onAddFilter(dimension)}
                    >
                      {i18n.t(FILTER_DIMENSION_LABEL_KEYS[dimension])}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              </WithAddIcon>
            )}
          </CustomField>
        )}
        <CustomField label='' name='outdoorDeviceQuery'>
          <Button
            variant='primary'
            size='sm'
            onClick={onQueryBtnClick}
            disabled={!!dayRangeError}
          >
            {i18n.t('report.buttons.search')}
          </Button>
        </CustomField>
      </>
    );
  }, [
    filterDimensions,
    filterDimensionsCanAdd,
    filterRangeExceeded,
    filterRangeHint,
    renderFilter,
    onAddFilter,

    startDate,
    endDate,
    from,
    to,
    gran,
    granOptions,
    metricType,
    metricTypeOptions,
    dayRangeError,
    onGranChange,
    onMetricTypeChange,
    renderScheduling,
    onQueryBtnClick,
    onDayRangeChange
  ]);

  const renderUserOptions = useCallback(() => {
    return (
      <>
        <ToggleButtonField
          label={i18n.t('weatherVerification.labels.reportType')}
          name='reportType'
          value={reportType}
          options={reportTypeOptions}
          onChange={onReportTypeChange}
        />
        {isWeatherVerification ? (
          <WeatherVerificationFilters
            model={weatherVerificationModel}
            maxDate={endDate}
          />
        ) : (
          renderPerformanceUserOptions()
        )}
      </>
    );
  }, [
    reportType,
    reportTypeOptions,
    isWeatherVerification,
    weatherVerificationModel,
    endDate,
    onReportTypeChange,
    renderPerformanceUserOptions
  ]);

  const getChartOptions = useCallback(
    (
      isEmpty: boolean,
      axisYInterval: number,
      labels: string[],
      currentMetricType: string
    ): ChartOptions<'bar'> => {
      const grid = {
        borderDash: [5, 2]
      };
      return {
        animation: {
          duration: 0
        },
        layout: {
          padding: {
            left: 5,
            right: 5
          }
        },
        maintainAspectRatio: false,
        scales: {
          x: {
            grid,
            offset: true,
            title: {
              display: true,
              text: i18n.t('outdoorDeviceReport.labels.timeSlot'),
              font: {
                size: 14,
                weight: 'bold'
              }
            },
            ticks: {
              maxRotation: 0,
              font: {
                size: 12
              },
              callback: function (tick, index, tickList) {
                const tickLength = Math.max(15, 100 / tickList.length);
                const label = labels[tick];
                return label.length > tickLength
                  ? `${label.substring(0, tickLength)}...`
                  : label;
              }
            }
          },
          left: {
            type: 'linear',
            position: 'left',
            grid,
            beginAtZero: true,
            title: {
              display: true,
              text: i18n.t(`outdoorDeviceMetric.labels.${currentMetricType}`),
              font: {
                size: 14,
                weight: 'bold'
              }
            },
            ticks: {
              stepSize: axisYInterval,
              font: {
                size: 12
              },
              maxTicksLimit: 5,
              callback: (tick: string | number) => {
                if (typeof tick === 'string' && isNaN(Number(tick))) {
                  return tick;
                }
                return commaDecorator(+tick);
              }
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: i18n.t(`outdoorDeviceReportChart.title.${currentMetricType}`),
            align: 'start',
            font: {
              size: 18,
              weight: 'bold'
            },
            padding: {
              bottom: 15
            }
          },
          datalabels: {
            display: false
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            enabled: !isEmpty,
            callbacks: {
              title: (tooltipItems) => {
                if (!tooltipItems[0].label) {
                  return '';
                }
                return tooltipItems[0].label;
              },
              label: (tooltipItems) => {
                if (!tooltipItems.label) {
                  return '';
                }
                const metric = tooltipItems.dataset.label;
                return `${i18n.t(`outdoorDeviceMetric.labels.${metric}`)}: ${tooltipItems.formattedValue}`;
              }
            }
          },
          legend: {
            display: false
          }
        }
      };
    },
    []
  );

  const getMetricValueCeil = useCallback((value: number): number => {
    if (!value) {
      return 0;
    }
    const digits = value.toString().split('.')[0].length;
    const multiply = Math.pow(10, digits - 1);
    return Math.ceil(value / multiply) * multiply;
  }, []);

  const isEmpty = !tableSummaryRecord || loading;

  const renderChart = useCallback(() => {
    const metricType = queryOptions.metricType;
    const metricData: number[] = [];
    let metricMax = 0;
    const labels: string[] = [];
    chartData.forEach((data) => {
      const metricValue = data[metricType] as number;
      metricMax = metricMax > metricValue ? metricMax : metricValue;
      metricData.push(metricValue);
      labels.push(data.name);
    });
    const metricValueCeil = getMetricValueCeil(metricMax);
    const axisYInterval = metricValueCeil / 4;
    if (isEmpty || reportTableData.length === 1) {
      metricData.push(metricValueCeil - axisYInterval + 1);
    }

    const data = {
      labels,
      datasets: [
        {
          label: metricType,
          yAxisID: 'left',
          data: metricData,
          fill: false,
          borderColor: config.reportMetric1Color,
          backgroundColor: config.reportMetric1Color,
          lineTension: 0,
          borderWidth: 1
        }
      ]
    };
    const chartConfig = {
      data,
      options: getChartOptions(isEmpty, axisYInterval, labels, metricType)
    };
    return (
      <GenericChart<'bar'>
        type='bar'
        data={chartConfig.data}
        options={chartConfig.options}
        height={200}
      />
    );
  }, [
    isEmpty,
    chartData,
    queryOptions.metricType,
    reportTableData,
    getChartOptions,
    getMetricValueCeil
  ]);

  return (
    <Modal
      className={styles.outdoorDeviceReportModal}
      fullScreen={true}
      animation={false}
      secondaryButton={{
        title: i18n.t('common.buttons.close'),
        callback: onClose
      }}
      dismiss={onClose}
    >
      <div className={styles.container}>
        {(loading || weatherVerificationModel.loading) && <LoadingIndicator />}
        {renderTitle()}
        {showDimensionSelectArea && (
          <div className={styles.userOptions}>{renderUserOptions()}</div>
        )}
        {isWeatherVerification ? (
          <div className={styles.reportTable}>
            <WeatherVerificationResult model={weatherVerificationModel} />
          </div>
        ) : (
          <>
            {showReportChart && (
              <div className={styles.summary}>
                <div className={styles.total}>
                  <div className={styles.title}>
                    {queryOptions.metricType ===
                    OutdoorDeviceMetricType.E_PUBLISHER_IMPRES
                      ? i18n.t(
                          'outdoorDeviceReport.labels.ePublisherImpresTotal'
                        )
                      : i18n.t('outdoorDeviceReport.labels.impresTotal')}
                  </div>
                  <div className={styles.value}>
                    {commaDecorator(metricTotalSum)}
                  </div>
                </div>
                <div className={styles.chart}>{renderChart()}</div>
              </div>
            )}
            <div className={cx(styles.reportTable, { needTopBorder: isEmpty })}>
              <div className={exportStyles.exportRow}>
                <button
                  type='button'
                  className={exportStyles.exportButton}
                  onClick={onExportCsv}
                  disabled={!canExportCsv}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  {i18n.t('weatherVerification.labels.exportCsv')}
                </button>
              </div>
              <StickableTanstackTable
                keyField={KEY_COLUMN}
                data={reportTableData}
                columns={columns}
                noDataIndication={i18n.t('common.labels.noData')}
                defaultSorted={[
                  {
                    dataField: KEY_COLUMN,
                    order: 'desc'
                  }
                ]}
                hidePagination={true}
              />
            </div>
          </>
        )}
        {showBackToTop && (
          <button
            type='button'
            className={styles.backToTop}
            aria-label={i18n.t('outdoorDeviceReport.labels.backToTop')}
            title={i18n.t('outdoorDeviceReport.labels.backToTop')}
            onClick={backToTop}
          >
            <FontAwesomeIcon icon={faChevronUp} />
          </button>
        )}
      </div>
    </Modal>
  );
};
